import {
	ApiMixSuiFrensBody,
	ApiDynamicFieldsBody,
	MixSuiFrensEvent,
	SuiFrenObject,
	SuiFrenStats,
	DynamicFieldObjectsWithCursor,
	EventsInputs,
	StakeSuiFrenEvent,
	SuiNetwork,
	UnstakeSuiFrenEvent,
	Url,
	SuiFrenAttributes,
	CapyLabsAppObject,
	StakedSuiFrenInfo,
	DynamicFieldsInputs,
	Balance,
	SuiFrensSortOption,
	SuiFrenAccessoryObject,
	ApiOwnedSuiFrenAccessoriesBody,
	ApiOwnedSuiFrensBody,
	ApiOwnedStakedSuiFrensBody,
	ApiHarvestSuiFrenFeesBody,
	HarvestSuiFrenFeesEvent,
	ObjectId,
	CallerConfig,
} from "../../types";
import { SuiFren } from "./suiFren";
import { StakedSuiFren } from "./stakedSuiFren";
import { Caller } from "../../general/utils/caller";
import { Coin } from "../coin";
import { Helpers } from "../../general/utils";
import { AftermathApi } from "../../general/providers";

export class SuiFrens extends Caller {
	// =========================================================================
	//  Constants
	// =========================================================================

	public static readonly constants = {
		mixingFeeCoinType: Coin.constants.suiCoinType,
		protocolFees: {
			mint: BigInt(250_000_000), // 0.25 SUI
			mixOwned: BigInt(250_000_000), // 0.25 SUI
			minMixStaked: BigInt(250_000_000), // 0.25 SUI
			mixStakedPercentage: 0.1, // 10%
		},
		suifrenFees: {
			mint: BigInt(8_000_000_000), // 8 SUI
		},
	};

	// =========================================================================
	//  Constructor
	// =========================================================================

	constructor(
		config?: CallerConfig,
		public readonly api?: AftermathApi
	) {
		super(config, "sui-frens");
	}

	// =========================================================================
	//  Public Methods
	// =========================================================================

	// =========================================================================
	//  Calculations
	// =========================================================================

	public static calcTotalInternalMixFee(inputs: {
		mixFee1: Balance | undefined;
		mixFee2: Balance | undefined;
	}): Balance {
		const { mixFee1, mixFee2 } = inputs;

		if (mixFee1 === undefined && mixFee2 === undefined)
			return this.constants.protocolFees.mixOwned;

		if (mixFee1 !== undefined && mixFee2 !== undefined) {
			return (
				this.calcMixFeeForStakedSuiFren({ mixFee: mixFee1 }) +
				this.calcMixFeeForStakedSuiFren({ mixFee: mixFee2 })
			);
		}

		return mixFee1 !== undefined
			? this.calcMixFeeForStakedSuiFren({ mixFee: mixFee1 })
			: mixFee2 !== undefined
				? this.calcMixFeeForStakedSuiFren({ mixFee: mixFee2 })
				: (() => {
						// to make TS happy :)
						throw new Error("unreachable");
					})();
	}

	private static calcMixFeeForStakedSuiFren(inputs: {
		mixFee: Balance;
	}): Balance {
		const { mixFee } = inputs;

		return (
			mixFee +
			Helpers.maxBigInt(
				this.constants.protocolFees.minMixStaked,
				mixFee /
					BigInt(
						Math.floor(this.constants.protocolFees.mixStakedPercentage * 100)
					)
			)
		);
	}

	// =========================================================================
	//  Class Objects
	// =========================================================================

	public async getSuiFren(inputs: { suiFrenObjectId: ObjectId }) {
		const suiFrens = await this.getSuiFrens({
			suiFrenObjectIds: [inputs.suiFrenObjectId],
		});
		return suiFrens[0];
	}

	public async getSuiFrens(inputs: { suiFrenObjectIds: ObjectId[] }) {
		const suiFrens = await this.fetchApi<SuiFrenObject[]>(
			`${JSON.stringify(inputs.suiFrenObjectIds)}`
		);
		return suiFrens.map((suiFren) => new SuiFren(suiFren, this.config));
	}

	public async getOwnedSuiFrens(inputs: ApiOwnedSuiFrensBody) {
		const ownedSuiFrens = await this.fetchApi<
			SuiFrenObject[],
			ApiOwnedSuiFrensBody
		>(`owned-sui-frens`, inputs);

		return ownedSuiFrens.map(
			(suiFren) => new SuiFren(suiFren, this.config, false, true)
		);
	}

	public async getOwnedStakedSuiFrens(inputs: ApiOwnedStakedSuiFrensBody) {
		const stakesInfo = await this.fetchApi<
			StakedSuiFrenInfo[],
			ApiOwnedStakedSuiFrensBody
		>(`owned-staked-sui-frens`, inputs);

		return stakesInfo.map((info) => new StakedSuiFren(info, this.config, true));
	}

	public async getAllStakedSuiFrens(
		inputs: {
			attributes: Partial<SuiFrenAttributes>;
			sortBy?: SuiFrensSortOption;
		} & DynamicFieldsInputs
	): Promise<DynamicFieldObjectsWithCursor<StakedSuiFren>> {
		const stakesInfoWithCursor = await this.fetchApi<
			DynamicFieldObjectsWithCursor<StakedSuiFrenInfo>,
			ApiDynamicFieldsBody
		>(
			`filtered-staked-sui-frens/${SuiFrens.createStakedSuiFrensQueryString(
				inputs
			)}`,
			inputs
		);

		const suiFrens = stakesInfoWithCursor.dynamicFieldObjects.map(
			(info) => new StakedSuiFren(info, this.config)
		);
		return {
			dynamicFieldObjects: suiFrens,
			nextCursor: stakesInfoWithCursor.nextCursor,
		};
	}

	public async getStakedSuiFrens(inputs: { stakedSuiFrenIds: ObjectId[] }) {
		const suiFrenInfos = await this.fetchApi<StakedSuiFrenInfo[]>(
			`staked-sui-frens/${JSON.stringify(inputs.stakedSuiFrenIds)}`
		);
		return suiFrenInfos.map((info) => new StakedSuiFren(info, this.config));
	}

	// =========================================================================
	//  Objects
	// =========================================================================

	public async getCapyLabsApp() {
		return this.fetchApi<CapyLabsAppObject>(`capy-labs-app`);
	}

	public async getOwnedAccessories(inputs: ApiOwnedSuiFrenAccessoriesBody) {
		return this.fetchApi<
			SuiFrenAccessoryObject[],
			ApiOwnedSuiFrenAccessoriesBody
		>("owned-accessories", inputs);
	}

	// =========================================================================
	//  Events
	// =========================================================================

	public async getHarvestFeesEvents(inputs: EventsInputs) {
		return this.fetchApiEvents<HarvestSuiFrenFeesEvent>(
			"events/harvest-fees",
			inputs
		);
	}

	public async getMixEvents(inputs: EventsInputs) {
		return this.fetchApiEvents<MixSuiFrensEvent>("events/mix", inputs);
	}

	public async getStakeEvents(inputs: EventsInputs) {
		return this.fetchApiEvents<StakeSuiFrenEvent>("events/stake", inputs);
	}

	public async getUnstakeEvents(inputs: EventsInputs) {
		return this.fetchApiEvents<UnstakeSuiFrenEvent>("events/unstake", inputs);
	}

	// =========================================================================
	//  Transactions
	// =========================================================================

	public async getMixTransaction(inputs: ApiMixSuiFrensBody) {
		return this.suiFrensApi().fetchBuildMixTx(inputs);
	}

	public async getHarvestFeesTransaction(inputs: ApiHarvestSuiFrenFeesBody) {
		return this.suiFrensApi().fetchBuildHarvestFeesTx(inputs);
	}

	// =========================================================================
	//  Inspections
	// =========================================================================

	public async getStats(): Promise<SuiFrenStats> {
		return this.fetchApi("stats");
	}

	// =========================================================================
	//  Public Static Methods
	// =========================================================================

	// =========================================================================
	//  Helpers
	// =========================================================================

	public static suiFren(
		suiFren: SuiFren | StakedSuiFren | undefined
	): SuiFren | undefined {
		return suiFren instanceof SuiFren ? suiFren : suiFren?.suiFren;
	}

	public static suiFrenId(
		suiFren: SuiFren | StakedSuiFren | undefined
	): ObjectId | undefined {
		return suiFren?.suiFren instanceof SuiFren
			? suiFren?.suiFren?.suiFren.objectId
			: suiFren?.suiFren?.objectId;
	}

	public static mixFee(
		suiFren: SuiFren | StakedSuiFren | undefined
	): Balance | undefined {
		return suiFren instanceof StakedSuiFren ? suiFren?.mixFee() : undefined;
	}

	// =========================================================================
	//  Private Static Methods
	// =========================================================================

	// =========================================================================
	//  Helpers
	// =========================================================================

	private static createStakedSuiFrensQueryString(inputs: {
		attributes: Partial<SuiFrenAttributes>;
		sortBy?: SuiFrensSortOption;
	}) {
		const { attributes, sortBy } = inputs;

		const startStr = sortBy ? `?sort=${sortBy}` : "";

		return Object.keys(attributes).length === 0
			? startStr
			: (startStr === "" ? "?" : startStr) +
					Object.entries(attributes)
						.map(
							([key, val], i) =>
								`${i === 0 && startStr === "" ? "" : "&"}${key}=${val}`
						)
						.reduce((acc, curr) => acc + curr, "");
	}

	// =========================================================================
	//  Private Helpers
	// =========================================================================

	private suiFrensApi = () => {
		const suiFrens = this.api?.SuiFrens();
		if (!suiFrens) {
			throw new Error("missing AftermathApi instance");
		}
		return suiFrens;
	};
}
